export interface Point {
  x: number;
  y: number;
}

export interface Bounds {
  width: number;
  height: number;
}

/** Inset from edge so handle circles aren't clipped by overflow:hidden */
const HANDLE_INSET = 2;

/** How far beyond the bounds the line extends for endpoint handles */
const LINE_OVERSHOOT = 20;

/**
 * Clip a ray from center in a given direction to the edge of a rectangle,
 * inset by HANDLE_INSET so handles remain fully visible.
 */
const clipToBoundsEdge = (
  centerX: number,
  centerY: number,
  dx: number,
  dy: number,
  width: number,
  height: number
): Point => {
  if (dx === 0 && dy === 0) return { x: centerX, y: centerY };

  const minX = HANDLE_INSET;
  const minY = HANDLE_INSET;
  const maxX = width - HANDLE_INSET;
  const maxY = height - HANDLE_INSET;

  // Find the smallest positive t that puts the point on an inset edge
  let t = Infinity;

  if (dx !== 0) {
    const tRight = (maxX - centerX) / dx;
    const tLeft = (minX - centerX) / dx;
    if (tRight > 0) t = Math.min(t, tRight);
    if (tLeft > 0) t = Math.min(t, tLeft);
  }
  if (dy !== 0) {
    const tBottom = (maxY - centerY) / dy;
    const tTop = (minY - centerY) / dy;
    if (tBottom > 0) t = Math.min(t, tBottom);
    if (tTop > 0) t = Math.min(t, tTop);
  }

  if (!isFinite(t)) t = 0;

  return {
    x: Math.max(minX, Math.min(maxX, centerX + dx * t)),
    y: Math.max(minY, Math.min(maxY, centerY + dy * t))
  };
};

/**
 * Extend a ray from center beyond the edge of a rectangle by LINE_OVERSHOOT.
 */
const extendBeyondBounds = (
  centerX: number,
  centerY: number,
  dx: number,
  dy: number,
  width: number,
  height: number
): Point => {
  if (dx === 0 && dy === 0) return { x: centerX, y: centerY };

  let t = Infinity;

  if (dx !== 0) {
    const tRight = (width - centerX) / dx;
    const tLeft = -centerX / dx;
    if (tRight > 0) t = Math.min(t, tRight);
    if (tLeft > 0) t = Math.min(t, tLeft);
  }
  if (dy !== 0) {
    const tBottom = (height - centerY) / dy;
    const tTop = -centerY / dy;
    if (tBottom > 0) t = Math.min(t, tBottom);
    if (tTop > 0) t = Math.min(t, tTop);
  }

  if (!isFinite(t)) t = 0;

  const len = Math.sqrt(dx * dx + dy * dy);
  const overshootT = len > 0 ? t + LINE_OVERSHOOT / len : t;

  return {
    x: centerX + dx * overshootT,
    y: centerY + dy * overshootT
  };
};

/**
 * Convert a CSS gradient angle to inner endpoints (where 0%/100% stops sit, clamped
 * to the element bounds) and extended endpoints (where the line visually extends to,
 * beyond the bounds, for endpoint handle placement).
 *
 * CSS angles: 0deg = bottom-to-top, 90deg = left-to-right, etc. (clockwise)
 */
export const angleToEndpoints = (
  angle: number,
  bounds: Bounds
): { start: Point; end: Point; extStart: Point; extEnd: Point } => {
  const { width, height } = bounds;
  const centerX = width / 2;
  const centerY = height / 2;

  const cssRad = (angle * Math.PI) / 180;
  const dx = Math.sin(cssRad);
  const dy = -Math.cos(cssRad);

  return {
    // Inner endpoints — stops are positioned along this segment
    start: clipToBoundsEdge(centerX, centerY, -dx, -dy, width, height),
    end: clipToBoundsEdge(centerX, centerY, dx, dy, width, height),
    // Extended endpoints — line drawn to here, endpoint handles placed here
    extStart: extendBeyondBounds(centerX, centerY, -dx, -dy, width, height),
    extEnd: extendBeyondBounds(centerX, centerY, dx, dy, width, height)
  };
};

/**
 * Convert two endpoint coordinates back to a CSS gradient angle (0-360).
 */
export const endpointsToAngle = (start: Point, end: Point): number => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;

  let cssAngle = (Math.atan2(dx, -dy) * 180) / Math.PI;
  cssAngle = ((cssAngle % 360) + 360) % 360;
  return Math.round(cssAngle);
};

/**
 * Convert a pixel position within bounds to percentage coordinates (0-100).
 */
export const centerToPosition = (
  point: Point,
  bounds: Bounds
): { x: number; y: number } => ({
  x: bounds.width > 0 ? (point.x / bounds.width) * 100 : 50,
  y: bounds.height > 0 ? (point.y / bounds.height) * 100 : 50
});

/**
 * Convert percentage coordinates (0-100) to pixel position within bounds.
 */
export const positionToCenter = (
  x: number,
  y: number,
  bounds: Bounds
): Point => ({
  x: (x / 100) * bounds.width,
  y: (y / 100) * bounds.height
});

/**
 * Compute a point along a line segment at a given percentage (0-100).
 * 0% = start, 100% = end.
 */
export const pointAlongLine = (
  start: Point,
  end: Point,
  percent: number
): Point => {
  const t = percent / 100;
  return {
    x: start.x + (end.x - start.x) * t,
    y: start.y + (end.y - start.y) * t
  };
};

/**
 * Project a point onto a line segment and return the percentage (0-100)
 * along the line where the projection falls. Clamped to [0, 100].
 */
export const projectPointOntoLine = (
  point: Point,
  start: Point,
  end: Point
): number => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSq = dx * dx + dy * dy;

  if (lengthSq === 0) return 0;

  const t = ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSq;
  return Math.round(Math.max(0, Math.min(100, t * 100)));
};

/**
 * Compute the radius for the radial gradient ring.
 * Returns the distance from center to the farthest corner of the bounds.
 */
export const computeRadialRadius = (center: Point, bounds: Bounds): number => {
  const corners = [
    { x: 0, y: 0 },
    { x: bounds.width, y: 0 },
    { x: 0, y: bounds.height },
    { x: bounds.width, y: bounds.height }
  ];
  let maxDist = 0;
  for (const corner of corners) {
    const dx = corner.x - center.x;
    const dy = corner.y - center.y;
    maxDist = Math.max(maxDist, Math.sqrt(dx * dx + dy * dy));
  }
  return maxDist;
};
